import { useLazyQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import Button from '../../components/Button';
import Form, { Field, Submit } from '../../components/Form';
import LoadingIndicator from '../../components/LoadingIndicator';
import MetaList, { MetaListItem } from '../../components/MetaList';
import styles from './CouponCodeInput.module.scss';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

const COUPON_CODE_INPUT_BILLING_COUPON_FRAGMENT = gql(`
  fragment CouponCodeInput_billingCoupon on BillingCoupon {
    id
    name
    amountOff
    percentOff
  }
`);

type BillingCouponFragment = DocumentType<
  typeof COUPON_CODE_INPUT_BILLING_COUPON_FRAGMENT
>;

const COUPON_QUERY = gql(`
  query getCoupon($id: ID!) {
    billingCoupon(id: $id) {
      ...CouponCodeInput_billingCoupon
    }
  }
`);

interface CouponCodeInputProps {
  billingCoupon?: BillingCouponFragment | null;
  onApply: (billingCoupon: BillingCouponFragment | null) => void;
}

const CouponCodeInput = ({ billingCoupon, onApply }: CouponCodeInputProps) => {
  const [checkCoupon, { loading, error }] = useLazyQuery(COUPON_QUERY, {
    onCompleted: (data) => {
      onApply(data.billingCoupon);
    },
    onError: () => {
      onApply(null);
    },
  });

  // Auto-fetch coupon if discount details are not present.
  // This happens when the coupon is pre-applied during trial.
  useEffect(() => {
    if (
      billingCoupon &&
      !billingCoupon.amountOff &&
      !billingCoupon.percentOff
    ) {
      checkCoupon({ variables: { id: billingCoupon.id } });
    }
  }, [billingCoupon, checkCoupon]);

  if (loading) {
    return (
      <div className={styles.loadingWrapper}>
        <LoadingIndicator delay={0} />
      </div>
    );
  }

  if (billingCoupon) {
    return (
      <MetaList>
        <MetaListItem>Coupon applied</MetaListItem>
        <MetaListItem>{billingCoupon.name}</MetaListItem>
        <MetaListItem>
          <Button variant="link" onClick={() => onApply(null)}>
            Remove
          </Button>
        </MetaListItem>
      </MetaList>
    );
  }

  return (
    <Form<{ id: string }>
      initialValues={{ id: '' }}
      onSubmit={({ id }) => checkCoupon({ variables: { id } })}
    >
      <div className={styles.formContainer}>
        <Field
          className={styles.input}
          margin="none"
          name="id"
          placeholder="Enter a coupon code"
          minLength={4}
          required
        />
        <Submit variant="secondary">Apply</Submit>
      </div>
      {error && (
        <div className={styles.error}>
          Hmm… We couldn&apos;t find this coupon. Our annual billing plans are
          always discounted.
        </div>
      )}
    </Form>
  );
};

export default CouponCodeInput;
